import { useNavigate } from 'react-router-dom';
import { Bot, Trash2 } from 'lucide-react';
import { useRelativeTime } from '../../hooks/useRelativeTime';
import { useNotifications } from '../../contexts/NotificationContext';
import { destinationPath } from '../../utils/notificationRoutes';
import { getInitials, getAvatarColors } from '../../utils/avatar';

/** Bottom-right severity dot color, by severity level. */
const SEVERITY_DOT = {
  INFO:             'bg-blue-500',
  WARNING:          'bg-amber-500',
  ACTION_REQUIRED:  'bg-red-500',
};

export function NotificationRow({ notification, compact = false, onClick }) {
  const navigate = useNavigate();
  const { markRead, softDelete } = useNotifications();
  const timeLabel = useRelativeTime(notification.createdAt);
  const dotClass = SEVERITY_DOT[notification.severity] ?? SEVERITY_DOT.INFO;
  const unread = notification.readAt == null;
  const actorName = notification.actorName;
  const hasActor = typeof actorName === 'string' && actorName.trim().length > 0;
  const avatarColors = hasActor ? getAvatarColors(actorName) : null;
  const initials = hasActor ? getInitials(actorName) : null;

  const handleClick = async () => {
    if (unread) {
      markRead(notification.id);
    }
    const path = destinationPath(notification.entityType, notification.entityId);
    if (path) navigate(path);
    if (onClick) onClick();
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    softDelete(notification.id);
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      className={[
        'group relative flex w-full items-start gap-3 px-4 text-left transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500 dark:focus-visible:ring-carbon-400',
        compact ? 'py-2.5' : 'py-3',
        unread
          ? 'bg-primary-50/50 hover:bg-primary-50 dark:bg-carbon-400/[0.07] dark:hover:bg-carbon-400/[0.12]'
          : 'hover:bg-slate-50 dark:hover:bg-white/[0.04]',
      ].join(' ')}
    >
      {unread && <span className="absolute inset-y-0 left-0 w-0.5 bg-primary-500 dark:bg-carbon-400" aria-hidden />}
      <span
        className="relative mt-0.5 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full"
        title={hasActor ? actorName : 'System notification'}
      >
        {hasActor ? (
          <span
            className={`inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold ${avatarColors.bg} ${avatarColors.text}`}
            aria-hidden
          >
            {initials}
          </span>
        ) : (
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-slate-200 dark:bg-slate-700" aria-hidden>
            <Bot className="h-4 w-4 text-slate-500 dark:text-slate-300" />
          </span>
        )}
        <span
          className={`absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full ring-2 ring-white dark:ring-slate-950 ${dotClass}`}
          aria-label={`Severity: ${notification.severity}`}
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className={`block text-sm ${unread ? 'font-semibold text-slate-900 dark:text-white' : 'text-slate-600 dark:text-slate-300'}`}>
          {notification.summary}
        </span>
        <span className="mt-0.5 block font-ledger-mono text-xs text-slate-500 dark:text-slate-400">{timeLabel}</span>
      </span>
      <span className="flex flex-shrink-0 items-center gap-2">
        {unread && <span className="h-2 w-2 rounded-full bg-primary-500 dark:bg-carbon-400" aria-label="Unread" />}
        <span
          onClick={handleDelete}
          className="rounded p-1 text-slate-500 opacity-0 transition-opacity hover:bg-slate-200 group-hover:opacity-100 dark:text-slate-400 dark:hover:bg-white/10"
          role="button"
          tabIndex={0}
          aria-label="Delete notification"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </span>
      </span>
    </button>
  );
}
